// Splash potion burst (MB.6; MB.4-remainder). PURE (ENG-1).
//
// A thrown splash potion shatters on impact into an area cloud: every living
// entity within SPLASH_RADIUS of the break point receives the potion's effect,
// its strength scaled by proximity exactly as Java does — full at the point of
// impact, fading linearly to nothing at the rim. Duration potions have their
// remaining ticks scaled; instant potions (healing/harming) keep the same
// effect but their magnitude is scaled by the returned `potency`.
//
// The witch (MOB-2 ranged) throws harmful splashes at the player; the same math
// serves a player-thrown splash later. The per-tick flight + shatter wiring runs
// in EntitySystem.tickProjectiles (it needs the world + player list); this module
// holds the pure falloff so it is unit-testable in isolation.
import { isInstantEffect } from './effects';

/** Radius (blocks) of the splash cloud. Java breaks at 4 blocks. */
export const SPLASH_RADIUS = 4.0;

/**
 * Proximity strength of a splash at distance `dist` from the shatter point:
 * 1 at the centre, 0 at or beyond the rim, linear in between (Java's 1 − d/R).
 * A target exactly on the break point gets the full effect.
 */
export function splashPotency(dist: number, radius = SPLASH_RADIUS): number {
  if (dist >= radius) return 0;
  if (dist <= 0) return 1;
  return 1 - dist / radius;
}

export interface SplashTarget { idx: number; x: number; y: number; z: number; }

export interface SplashHit {
  idx: number;
  effect: string;
  amplifier: number;
  /** Scaled remaining duration in ticks (0 for instant effects). */
  ticks: number;
  instant: boolean;
  /** Proximity factor (0..1) — instant potions scale their magnitude by this. */
  potency: number;
}

/**
 * Resolve a splash of `{effect, amplifier, baseTicks}` bursting at (ox,oy,oz)
 * against `targets`: one SplashHit per target inside SPLASH_RADIUS, its duration
 * scaled by proximity (≥ 1 tick so a grazing hit still lands one tick). Targets
 * outside the radius are omitted. Deterministic and order-preserving.
 */
export function splashBurst(
  effect: string,
  amplifier: number,
  baseTicks: number,
  ox: number,
  oy: number,
  oz: number,
  targets: readonly SplashTarget[],
): SplashHit[] {
  const instant = isInstantEffect(effect);
  const hits: SplashHit[] = [];
  for (const t of targets) {
    const d = Math.hypot(t.x - ox, t.y - oy, t.z - oz);
    const potency = splashPotency(d);
    if (potency <= 0) continue;
    const ticks = instant ? 0 : Math.max(1, Math.round(baseTicks * potency));
    hits.push({ idx: t.idx, effect, amplifier, ticks, instant, potency });
  }
  return hits;
}
